/*
 *  -----------------------------------------------------------------------
 *    <copyright file="risk-overview-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { orderBy } from '@progress/kendo-data-query';
import { getRiskOverviewGridColumns } from '@components/central-monitoring/study-summary/risk-overview-scrollable-grid/risk-overview-scrollable-grid.helper';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { useTranslation } from 'hooks';
import { createRiskOverviewGrid } from 'utils/data-table.service';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { ExportExcelComponentInputColumn } from 'views/components/core/export-excel';
import './kri-scores-grid.scss';
import { AllRoutes } from '@utils/router/all-routes';

interface RiskOverviewColumn extends ExportExcelComponentInputColumn {
    text?: string;
}

const RiskOverviewGrid = ({
    excelExport = false,
    hideColumnMenu = false,
    showActions = true,
    studyData,
    config = {},
    sitesStatusesToExclude,
    riskOverviews,
    riskOverviewColumns,
    isExcludeSites = true,
}: {
    excelExport?: boolean;
    hideColumnMenu?: boolean;
    showActions?: boolean;
    studyData: any;
    config: any;
    sitesStatusesToExclude: string[];
    riskOverviews: any[];
    riskOverviewColumns: any[];
    isExcludeSites?: boolean;
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [columns, setColumns] = useState([]);
    const [items, setItems] = useState([]);

    const rowActions = (item: any) => [
        {
            label: 'cm:viewSiteDashboard',
            icon: ['far', 'eye'],
            onClick: () => {
                NavigationService.setViewLegacySiteDashboard();
                navigate(AllRoutes.path('cm-site-dashboard') as string, {
                    state: {
                        skipSelectDate: true,
                        showDashboard: true,
                        siteId: item.id as string | null | undefined,
                    },
                });
            },
            enabled: sitesStatusesToExclude?.indexOf(item.status) < 0 ?? true,
        },
    ];

    const createGridColumns = () => getRiskOverviewGridColumns(config, riskOverviewColumns, t);

    function createGridData(gridColumns: any[]) {
        let sites = createRiskOverviewGrid(studyData, riskOverviews, sitesStatusesToExclude, gridColumns);

        if (isExcludeSites) sites = sites.filter((item: any) => item.siteToShow);

        return orderBy(sites, [
            { field: 'siteToShow', dir: 'desc' },
            { field: 'score', dir: 'desc' },
        ]);
    }

    const createDataForGrid = () => {
        const gridColumns = createGridColumns();
        const gridData = createGridData(gridColumns);

        return {
            columns: gridColumns,
            data: gridData,
        };
    };

    const mapColumns = (inputColumns: RiskOverviewColumn[]) =>
        inputColumns
            .filter((x: any) => x.show === true)
            .map(
                (column: RiskOverviewColumn) =>
                    ({
                        title: column.text || column.title,
                        field: column.field === 'siteActiveDate' ? `_${column.field}` : column.field,
                    }) as ExportExcelComponentInputColumn,
            );

    useEffect(() => {
        if (riskOverviewColumns && riskOverviews?.length) {
            const result = createDataForGrid();
            setColumns(result.columns);
            setItems(result.data);
        }
    }, [riskOverviews, riskOverviewColumns]);

    if (!studyData) return <h3>No data available</h3>;

    return (
        <ExpandableGrid
            customKey="study-summary-grid"
            id="studySummaryGrid"
            items={items}
            groupable
            showPager
            scrollable="scrollable"
            reorderable
            resizable
            addScroll={false}
            pageable={false}
            hideColumnMenu={hideColumnMenu}
            rowActions={showActions ? rowActions : undefined}
            options={{
                allowExcelExport: excelExport,
                allowExpandCollapseAll: false,
                useFilterChips: false,
            }}
            excelExportOptions={{
                name: 'Risk Overview',
                fileName: 'risk-overview',
                mapColumns,
            }}
            detail={null}
            columns={columns}
        />
    );
};

export default RiskOverviewGrid;
